<template>
  <div class="min-vh-100" :data-theme="theme">
    <header class="container py-3 d-flex align-items-center justify-content-between">
        <router-link class="" to="/">
      <div class="d-flex align-items-center gap-2">
        <img src="/assets/logo.svg" alt="Flat 18" height="46" />
      </div></router-link>
      <!-- Desktop nav -->
      <div class="d-none d-lg-flex align-items-center gap-3">
        <router-link class="text-decoration-none link-body-emphasis nav-affordance" to="/">Home</router-link>
        <router-link class="text-decoration-none link-body-emphasis nav-affordance" to="/swap">Swap</router-link>
        <router-link class="text-decoration-none link-body-emphasis nav-affordance" to="/docs">Docs</router-link>
        <router-link class="text-decoration-none link-body-emphasis nav-affordance" to="/admin">Admin</router-link>
        <div ref="walletMenuRef" class="position-relative">
          <span :class="['badge', 'menu-badge', chainBadgeClass]" role="button" @click="toggleWalletMenu" style="cursor:pointer">
            <template v-if="wallet.address">
              {{ networkName }} · {{ shortAddress }}
            </template>
            <template v-else>
              Connect Wallet
            </template>
            <i class="bi" :class="showWalletMenu ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
          </span>
          <div v-if="showWalletMenu" class="dropdown-menu dropdown-menu-end show mt-1" style="display:block; position:absolute; right:0; top:100%; min-width: 240px;">
            <!-- Connection section -->
            <button v-if="!wallet.isConnected" class="dropdown-item d-flex align-items-center gap-2" @click="onConnectClick">
              <i class="bi bi-wallet2"></i>
              Connect Wallet
            </button>
            <button v-else class="dropdown-item d-flex align-items-center gap-2" @click="openWalletModal">
              <i class="bi bi-gear"></i>
              Wallet Settings
            </button>
            <div class="dropdown-divider"></div>

            <!-- Network controls -->
            <div class="px-3 py-2 small text-muted-token">Network</div>
            <div class="dropdown-item d-flex align-items-center justify-content-between gap-2">
              <span>Testnet mode</span>
              <div class="form-check form-switch m-0">
                <input class="form-check-input" type="checkbox" id="menuModeSwitch" :checked="isTestnet" @change="onModeSwitchChange" />
              </div>
            </div>
            <div class="px-3 py-2">
              <select class="form-select form-select-sm" v-model.number="selectedChainId" @change="onChainChange">
                <option v-for="c in chains" :key="c.id" :value="c.id">{{ c.label }}</option>
              </select>
            </div>
            <div class="dropdown-divider"></div>

            <!-- Address actions -->
            <button v-if="wallet.address" class="dropdown-item d-flex align-items-center gap-2" @click="copyAddress">
              <i class="bi bi-clipboard"></i>
              Copy Address
            </button>
            <a v-if="wallet.address" class="dropdown-item d-flex align-items-center gap-2" :href="explorerAddress" target="_blank" rel="noopener">
              <i class="bi bi-box-arrow-up-right"></i>
              {{ explorerLabel }}
            </a>
            <button v-if="wallet.address" class="dropdown-item text-danger d-flex align-items-center gap-2" @click="onDisconnectClick">
              <i class="bi bi-x-circle"></i>
              Disconnect
            </button>
          </div>
        </div>
        <button class="badge btn btn-sm btn-primary" @click="toggleTheme" aria-label="Toggle color theme">
          <i class="bi bi-moon-stars" v-if="theme==='light'"></i>
          <i class="bi bi-sun" v-else></i>
        </button>
      </div>
      <!-- Mobile menu toggle -->
      <button
        class="btn btn-outline-secondary d-flex d-lg-none align-items-center justify-content-center"
        type="button"
        aria-label="Toggle navigation"
        :aria-expanded="mobileMenuOpen ? 'true' : 'false'"
        aria-controls="mobile-menu"
        @click="mobileMenuOpen = !mobileMenuOpen"
      >
        <i class="bi" :class="mobileMenuOpen ? 'bi-x' : 'bi-list'" style="font-size: 1.25rem;"></i>
      </button>
    </header>
    <!-- Mobile nav panel -->
    <nav v-if="mobileMenuOpen" id="mobile-menu" class="container d-lg-none mb-3">
      <div class="card p-3 gap-2">
        <router-link class="text-decoration-none py-2" to="/" @click="mobileMenuOpen=false">Home</router-link>
        <router-link class="text-decoration-none py-2" to="/swap" @click="mobileMenuOpen=false">Swap</router-link>
        <router-link class="text-decoration-none py-2" to="/docs" @click="mobileMenuOpen=false">Docs</router-link>
        <router-link class="text-decoration-none py-2" to="/admin" @click="mobileMenuOpen=false">Admin</router-link>
        <div class="border-top my-2"></div>
        <button
          class="btn btn-primary w-100"
          v-if="!wallet.isConnected"
          @click="onConnectClick"
        >
          <i class="bi bi-wallet2 me-1"></i> Connect Wallet
        </button>
        <div v-else class="d-grid gap-2">
          <button class="btn btn-outline-secondary w-100" @click="openWalletModal">
            <i class="bi bi-gear me-1"></i> Wallet Settings
          </button>
          <button class="btn btn-outline-danger w-100" @click="onDisconnectClick">
            <i class="bi bi-x-circle me-1"></i> Disconnect
          </button>
        </div>
        <div class="d-flex align-items-center justify-content-between mt-2 small text-muted-token">
          <div class="d-flex align-items-center gap-2">
            <span>Theme</span>
            <button class="btn btn-sm btn-outline-secondary" @click="toggleTheme" aria-label="Toggle color theme">
              <i class="bi bi-moon-stars" v-if="theme==='light'"></i>
              <i class="bi bi-sun" v-else></i>
            </button>
          </div>
          <div class="form-check form-switch m-0">
            <input class="form-check-input" type="checkbox" id="menuModeSwitchMobile" :checked="isTestnet" @change="onModeSwitchChange" />
            <label class="form-check-label" for="menuModeSwitchMobile">Testnet</label>
          </div>
        </div>
      </div>
    </nav>
    <main><router-view /></main>
    <footer class="container py-4 text-secondary small">
      For demonstration only. Not financial advice. Non‑custodial; you retain full control of your wallet. Network fees may apply. Always verify contract addresses.
    </footer>
    <!-- Toasts -->
    <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 9999">
      <div v-for="t in toasts" :key="t.id" class="toast show align-items-center text-bg-success border-0 mb-2" :class="{ 'text-bg-danger': t.type==='error', 'text-bg-info': t.type==='info' }">
        <div class="d-flex">
          <div class="toast-body">{{ t.message }}</div>
          <button type="button" class="btn-close btn-close-white me-2 m-auto" @click="removeToast(t.id)"></button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useToastStore } from './stores/toast';
import { appKitReady } from './lib/appkit';
import { useWalletStore } from './stores/wallet';
import { ThemeController } from '@reown/appkit-controllers';
import { useChainStore } from './stores/chain';
// Prefer saved theme; fall back to system preference
const getPreferredTheme = () => {
  const saved = localStorage.getItem('theme');
  if (saved === 'light' || saved === 'dark') return saved;
  const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : 'light';
};
const theme = ref(getPreferredTheme());
const toggleTheme = () => {
  theme.value = theme.value === 'dark' ? 'light' : 'dark';
  localStorage.setItem('theme', theme.value);
};
const toast = useToastStore();
const toasts = computed(() => toast.items);
const removeToast = (id: number) => toast.remove(id);
const isAppKit = computed(() => appKitReady());
const wallet = useWalletStore();
wallet.init();
const router = useRouter();
const chain = useChainStore();
chain.init();
const chains = computed(() => chain.supported);
const selectedChainId = ref<number>(chain.selectedId);
const TESTNET_ID = 11155111; // Sepolia
const DEFAULT_MAINNET_ID = Number((process.env.VUE_APP_DEFAULT_CHAIN_ID as string) || '1');
const isTestnet = computed(() => chain.selectedId === TESTNET_ID);
const lastMainnetId = ref<number>(isTestnet.value ? DEFAULT_MAINNET_ID : chain.selectedId);
const lastTestnetId = ref<number>(isTestnet.value ? chain.selectedId : TESTNET_ID);
async function onChainChange() {
  chain.select(selectedChainId.value);
  try {
    await wallet.ensureChain(chain.current.chain);
  } catch (_) {
    // ignore; user may reject network switch
  }
}
async function setMode(toTestnet: boolean) {
  const targetId = toTestnet ? (lastTestnetId.value || TESTNET_ID) : (lastMainnetId.value || DEFAULT_MAINNET_ID);
  selectedChainId.value = targetId;
  await onChainChange();
}
async function onModeSwitchChange(ev: Event) {
  const target = ev.target as HTMLInputElement;
  await setMode(!!target.checked);
}
async function connectWallet() {
  if (isAppKit.value) {
    try {
      const mod = await import('@reown/appkit/vue-core');
      const { open } = mod.useAppKit();
      await open();
      return;
    } catch (_) {
      // fall through to injected
    }
  }
  await wallet.openModal();
  if (!wallet.isConnected) {
    toast.push('error', 'Unable to open wallet modal. Set VUE_APP_WALLETCONNECT_PROJECT_ID or use injected wallet (MetaMask).');
  } else {
    toast.push('info', 'Connected with injected wallet');
  }
}

async function openWalletModal() {
  await router.push('/wallet');
  showWalletMenu.value = false;
}

async function disconnectWallet() {
  try {
    await wallet.disconnect();
    toast.push('info', 'Wallet disconnected');
  } catch (e) {
    toast.push('error', 'Failed to disconnect');
  }
}

// Ensure theme attributes are applied to :root so CSS variables and Bootstrap switch
const applyTheme = () => {
  document.documentElement.setAttribute('data-theme', theme.value);
  // Bootstrap 5.3+ color modes
  document.documentElement.setAttribute('data-bs-theme', theme.value);
  try { ThemeController.setThemeMode(theme.value as any); } catch {}
};
onMounted(applyTheme);
watch(theme, applyTheme);

// Remember last selected per mode for smoother toggling
watch(() => chain.selectedId, (id) => {
  selectedChainId.value = id;
  if (id === TESTNET_ID) lastTestnetId.value = id;
  else lastMainnetId.value = id;
});

const shortAddress = computed(() => wallet.address ? `${wallet.address.slice(0,6)}…${wallet.address.slice(-4)}` : '');
const networkName = computed(() => chain.current.label);
const chainBadgeClass = computed(() => chain.current.id === 11155111 ? 'badge-tinted-accent' : 'badge-tinted-primary');
const explorerAddress = computed(() => wallet.address ? `${chain.current.explorerBase || 'https://sepolia.etherscan.io'}/address/${wallet.address}` : '#');
const explorerLabel = computed(() => {
  const base = chain.current.explorerBase || '';
  return base.includes('etherscan') ? 'View on Etherscan' : 'View on Explorer';
});

// Wallet dropdown
const showWalletMenu = ref(false);
const walletMenuRef = ref<HTMLElement | null>(null);
const toggleWalletMenu = () => { showWalletMenu.value = !showWalletMenu.value; };
const onDocClick = (e: MouseEvent) => {
  const el = walletMenuRef.value;
  if (!el) return;
  if (!el.contains(e.target as Node)) showWalletMenu.value = false;
};
onMounted(() => document.addEventListener('click', onDocClick));
onBeforeUnmount(() => document.removeEventListener('click', onDocClick));

const onDisconnectClick = async () => {
  await disconnectWallet();
  showWalletMenu.value = false;
};
const onConnectClick = async () => {
  await connectWallet();
  showWalletMenu.value = false;
};
async function copyAddress() {
  if (!wallet.address) return;
  try {
    await navigator.clipboard.writeText(wallet.address);
    toast.push('info', 'Address copied');
  } catch {
    toast.push('error', 'Failed to copy');
  }
}

// Mobile menu
const mobileMenuOpen = ref(false);
// Close on route change
watch(() => router.currentRoute.value.fullPath, () => { mobileMenuOpen.value = false; });
// Close on upsize to lg+
function onResize() {
  if (window.innerWidth >= 992) mobileMenuOpen.value = false;
}
onMounted(() => window.addEventListener('resize', onResize));
onBeforeUnmount(() => window.removeEventListener('resize', onResize));
</script>

<style>
body { background: var(--bg); color: var(--fg); }
</style>
